import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFindMany, useFindOne, useAction, useGlobalAction, useUser } from "@gadgetinc/react";
import { api } from "../api";
import ScenarioDisplay from '../components/ScenarioDisplay';
import ShareScenarioModal from '../components/ShareScenarioModal';
import RunScenarioModal from '../components/RunScenarioModal';
import { VoiceSimShareModal } from '../components/VoiceSimShareModal';
import MediaStatusBadge from '../components/MediaStatusBadge';
import { canUserGenerateScenarios } from '../lib/roleUtils';
import { getScenarioAgeInfo, getScenarioChiefComplaint, getScenarioPathology, getScenarioSkillLevel } from "../lib/scenarioNaming";
import { FiShare2 } from 'react-icons/fi';
import { FaPlay } from 'react-icons/fa';
import {
  Box,
  Button,
  Container,
  Grid,
  Heading,
  Input,
  Select,
  Stack,
  Text,
  Card,
  CardBody,
  CardFooter,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  Switch,
  FormControl,
  FormLabel,
  HStack,
  VStack,
  Skeleton,
  SkeletonText,
  Alert,
  AlertIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useToast,
  Flex,
  Spacer,
  Divider,
  Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  AddIcon,
  ViewIcon,
  EditIcon,
  CopyIcon,
  DeleteIcon,
  ExternalLinkIcon,
  SearchIcon,
  ChevronDownIcon,
  ArrowBackIcon,
  RepeatIcon,
} from "@chakra-ui/icons";
import { FaClipboardList } from "react-icons/fa";

const LEVELS_OF_CARE = ["All", "EMT_Basic", "EMT_Intermediate", "Paramedic", "Critical_Care"];
const MAJOR_SYSTEMS = ["All", "Trauma", "Cardiac", "Respiratory", "Neurological", "Medical", "Pediatric", "OB/GYN"];
const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "difficulty", label: "Difficulty" },
  { value: "complaint", label: "Chief Complaint" },
  { value: "pathology", label: "Pathology" },
  { value: "age", label: "Age" },
  { value: "skill", label: "Skill Level" }
];

export default function MyScenarios() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [levelOfCareFilter, setLevelOfCareFilter] = useState("All");
  const [systemFilter, setSystemFilter] = useState("All");
  const [showPublic, setShowPublic] = useState(false);
  const [showHiddenScenarios, setShowHiddenScenarios] = useState(false);
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedScenario, setSelectedScenario] = useState(null);

  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useDisclosure();
  const { isOpen: isVoiceSimShareOpen, onOpen: onVoiceSimShareOpen, onClose: onVoiceSimShareClose } = useDisclosure();
  const cancelRef = React.useRef();

  const [openMenuId, setOpenMenuId] = useState(null);

  const [editDescription, setEditDescription] = useState("");
  const [shareScenario, setShareScenario] = useState(null);
  const [voiceSimScenario, setVoiceSimScenario] = useState(null);
  const [useCompactButtons, setUseCompactButtons] = useState(false);
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [runningScenario, setRunningScenario] = useState(null);
  const [regeneratingScenarios, setRegeneratingScenarios] = useState({});
  const user = useUser(api);

  // Fetch full user details with cohort memberships for access check
  const [{ data: fullUser, fetching: fetchingUser, error: userError }] = useFindOne(
    api.user,
    user?.id || "", // useFindOne requires an id string, not passed in options
    {
      select: {
        id: true,
        roles: {
          key: true,
          name: true
        },
        individualScenarioAccess: true,
        accountType: true,
        scenarioCredits: true,
        scenariosUsedThisYear: true,
        scenarioCreditsResetDate: true,
        subscriptionStatus: true,
        subscriptionExpiration: true,
        institution: true,
        cohortMemberships: {
          edges: {
            node: {
              id: true,
              active: true,
              hasScenarioGeneratorAccess: true,
              primaryRole: true,
              cohort: {
                id: true,
                name: true,
                displayName: true,
                grantScenarioAccessToInstructors: true,
              },
            },
          },
        },
      },
      pause: !user?.id, // Don't run the query if user.id is not available
    }
  );

  // Get full access details using the helper function
  const accessDetails = useMemo(() => {
    return canUserGenerateScenarios(fullUser);
  }, [fullUser]);

  const { 
    hasAccess: hasScenarioGeneratorAccess, 
    canViewEvals, 
    canSeeSharedScenarios, 
    source: accessSource, 
    institution: accessInstitution 
  } = accessDetails;

  // Parse credit sources
  const creditSourcesInfo = useMemo(() => {
    if (!fullUser) return { creditSources: [], hasSelf: false, institutionSources: [], hasMultipleSources: false };
    
    const creditSources = fullUser.scenarioCreditSources || [];
    const hasSelf = creditSources.includes('self');
    const institutionSources = creditSources
      .filter(s => s.startsWith('institution:'))
      .map(s => s.replace('institution:', ''));
    const hasMultipleSources = creditSources.length > 1;
    
    return { creditSources, hasSelf, institutionSources, hasMultipleSources };
  }, [fullUser]);

  const { creditSources, hasSelf, institutionSources, hasMultipleSources } = creditSourcesInfo;

  // Check if subscription is active
  const subscriptionActive = useMemo(() => {
    if (!fullUser) return false;
    
    // Check if subscription hasn't expired
    if (fullUser.subscriptionExpiration) {
      return new Date(fullUser.subscriptionExpiration) > new Date();
    }
    
    // Check cohort-level unlimited
    const memberships = fullUser.cohortMemberships?.edges?.map(edge => edge.node) || [];
    if (memberships.some(m => m.cohort?.unlimitedScenarios)) return true;
    
    // Default to true if no expiration set
    return true;
  }, [fullUser]);

  // Check if user has unlimited scenarios
  const hasUnlimitedScenarios = useMemo(() => {
    // System admins ALWAYS have unlimited scenarios
    if (user?.roles?.some(role => 
      role === 'admin' || role === 'system-admin' || 
      role?.key === 'admin' || role?.key === 'system-admin'
    )) {
      return true;
    }
    
    if (!fullUser) return false;
    
    // Cohort-level unlimited
    const memberships = fullUser.cohortMemberships?.edges?.map(edge => edge.node) || [];
    if (memberships.some(m => m.cohort?.unlimitedScenarios)) return true;
    
    // TODO: Check institution-level unlimited when institutionConfiguration is loaded
    
    return false;
  }, [user?.roles, fullUser]);

  // Extract cohorts where user is an instructor
  const userCohorts = useMemo(() => {
    if (!fullUser?.cohortMemberships?.edges) return [];
    
    return fullUser.cohortMemberships.edges
      .map(edge => edge.node)
      .filter(membership => membership.primaryRole === 'instructor')
      .map(membership => membership.cohort)
      .filter(Boolean);
  }, [fullUser]);

  // Check if user is an instructor
  const isInstructor = useMemo(() => {
    if (!fullUser?.cohortMemberships?.edges) return false;
    
    return fullUser.cohortMemberships.edges.some(
      edge => edge.node.primaryRole === 'instructor'
    );
  }, [fullUser]);

  const isAdminUser = useMemo(() => {
    return user?.roles?.some((role) =>
      role === "admin" ||
      role === "system-admin" ||
      role?.key === "admin" ||
      role?.key === "system-admin"
    );
  }, [user]);

  // Helper function to determine if a scenario is shared (created by another instructor in user's cohort)
  const isSharedScenario = (scenario) => {
    if (!user?.id || !scenario?.createdBy?.id) return false;
    if (scenario.createdBy.id === user.id) return false; // User's own scenario
    
    // Check if creator is in any of user's instructor cohorts
    const userCohortIds = userCohorts.map(c => c.id);
    const creatorCohortIds = scenario.createdBy.cohortMemberships?.edges
      ?.map(edge => edge.node.cohort?.id)
      .filter(Boolean) || [];
    
    return userCohortIds.some(id => creatorCohortIds.includes(id));
  };

  const normalizeInstitution = (value) => String(value || "").trim().toLowerCase();

  // Debug access check
  React.useEffect(() => {
    console.log('=== SCENARIO ACCESS DEBUG ===');
    console.log('User:', user);
    console.log('Access Details:', accessDetails);
    console.log('hasScenarioGeneratorAccess:', hasScenarioGeneratorAccess);
    console.log('canViewEvals:', canViewEvals);
    console.log('canSeeSharedScenarios:', canSeeSharedScenarios);
    console.log('accessSource:', accessSource);
    console.log('accessInstitution:', accessInstitution);
    console.log('subscriptionActive:', subscriptionActive);
    console.log('hasUnlimitedScenarios:', hasUnlimitedScenarios);
    console.log('============================');
    console.log('User roles check:', {
      userRoles: user?.roles,
      isSystemAdmin: user?.roles?.some(role => 
        role === 'system-admin' || role?.key === 'system-admin'
      ),
      rawUser: user
    });
  }, [user, accessDetails, hasScenarioGeneratorAccess, canViewEvals, canSeeSharedScenarios, accessSource, accessInstitution, subscriptionActive, hasUnlimitedScenarios]);

  // Detect window size for responsive button layout
  React.useEffect(() => {
    const checkWidth = () => {
      // Use compact layout for mobile and smaller screens
      setUseCompactButtons(window.innerWidth < 768);
    };
    
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  // Build filter object for API query
  const filterOptions = useMemo(() => {
    const filters = {};

    if (showPublic) {
      filters.isPublic = { equals: true };
    }
    // When not showing public, we'll fetch all scenarios and filter client-side
    // to include user's own scenarios + scenarios from instructors in same cohorts

    if (levelOfCareFilter !== "All") {
      filters.levelOfCare = { equals: levelOfCareFilter };
    }

    if (systemFilter !== "All") {
      filters.majorSystemInvolved = { equals: systemFilter };
    }

    return filters;
  }, [levelOfCareFilter, systemFilter, searchQuery, showPublic]);

  // Fetch scenarios with filters
  const [{ data, fetching, error }, refetch] = useFindMany(api.clinicalScenario, {
    filter: filterOptions,
    sort: { createdAt: "Descending" },
    // Fetch a larger window because we apply access scoping client-side.
    first: 200,
    select: {
      id: true,
      title: true,
      description: { markdown: true, truncatedHTML: true },
      levelOfCare: true,
      majorSystemInvolved: true,
      essentialSkills: true,
      scenarioOutput: true,
      ageRange: true,
      institution: true,
      isActive: true,
      isPublic: true,
      createdAt: true,
      createdBy: {
        id: true,
        firstName: true,
        lastName: true,
        email: true,
        cohortMemberships: {
          edges: {
            node: {
              cohort: {
                id: true,
              },
              primaryRole: true,
            },
          },
        },
      },
      ageBracket: true,
      difficulty: true,
      mechanismNatureOfInjury: true,
      mediaGenerationEnabled: true,
      mediaGenerationStatus: true,
      patientImageUrl: true,
      sceneArrivalVideoUrl: true,
      deteriorationVideoUrl: true,
      improvementVideoUrl: true,
    },
  });

  // Filter/sort scenarios client-side based on access mode and UI controls
  const filteredData = useMemo(() => {
    if (!data) return data;
    
    let scoped = [];

    if (showPublic) {
      scoped = data;
    } else if (!canSeeSharedScenarios) {
      scoped = data.filter((scenario) => scenario.createdBy?.id === user?.id);
    } else {
      const userInstitution = normalizeInstitution(accessInstitution || fullUser?.institution);
      scoped = data.filter((scenario) => {
        if (scenario.createdBy?.id === user?.id) return true;
        if (isSharedScenario(scenario)) return true;

        // Fallback when creator relationship data is restricted or unavailable.
        const scenarioInstitution = normalizeInstitution(scenario.institution);
        if (userInstitution && scenarioInstitution && scenarioInstitution === userInstitution) {
          return true;
        }

        return false;
      });
    }

    if (!showHiddenScenarios) {
      scoped = scoped.filter((scenario) => scenario.isActive !== false);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      scoped = scoped.filter((scenario) => {
        const title = String(scenario.title || "").toLowerCase();
        const complaint = getScenarioChiefComplaint(scenario).toLowerCase();
        const pathology = getScenarioPathology(scenario).toLowerCase();
        return title.includes(query) || complaint.includes(query) || pathology.includes(query);
      });
    }

    const sorted = [...scoped];
    sorted.sort((a, b) => {
      if (sortBy === "difficulty") return (b.difficulty || 0) - (a.difficulty || 0);
      if (sortBy === "complaint") return getScenarioChiefComplaint(a).localeCompare(getScenarioChiefComplaint(b));
      if (sortBy === "pathology") return getScenarioPathology(a).localeCompare(getScenarioPathology(b));
      if (sortBy === "age") return (getScenarioAgeInfo(a).numeric || -1) - (getScenarioAgeInfo(b).numeric || -1);
      if (sortBy === "skill") return getScenarioSkillLevel(a).localeCompare(getScenarioSkillLevel(b));
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return sorted;
  }, [data, showPublic, user?.id, canSeeSharedScenarios, userCohorts, showHiddenScenarios, searchQuery, sortBy, accessInstitution, fullUser?.institution]);

  const [updateResult, updateScenario] = useAction(api.clinicalScenario.update);
  const [deleteResult, deleteScenario] = useAction(api.clinicalScenario.delete);
  const [createResult, createScenario] = useAction(api.clinicalScenario.create);

  const handleViewScenario = (scenario) => {
    setSelectedScenario(scenario);
    onViewOpen();
  };

  const handleEditDescription = (scenario) => {
    setSelectedScenario(scenario);
    setEditDescription(scenario.description?.markdown || "");
    onEditOpen();
  };

  const handleSaveDescription = async () => {
    try {
      await updateScenario({
        id: selectedScenario.id,
        description: { markdown: editDescription },
      });
      toast({
        title: "Description updated",
        status: "success",
        duration: 3000,
      });
      onEditClose();
      refetch();
    } catch (err) {
      toast({
        title: "Error updating description",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleDuplicate = async (scenario) => {
    try {
      await createScenario({
        title: `${scenario.title} (Copy)`,
        description: scenario.description?.markdown ? { markdown: scenario.description.markdown } : null,
        levelOfCare: scenario.levelOfCare,
        majorSystemInvolved: scenario.majorSystemInvolved,
        essentialSkills: scenario.essentialSkills,
        scenarioOutput: scenario.scenarioOutput,
        ageRange: scenario.ageRange,
        institution: scenario.institution,
        isActive: scenario.isActive,
        isPublic: false,
        createdBy: { _link: scenario.createdBy.id },
      });
      toast({
        title: "Scenario duplicated",
        status: "success",
        duration: 3000,
      });
      refetch();
    } catch (err) {
      toast({
        title: "Error duplicating scenario",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteScenario({ id: selectedScenario.id });
      toast({
        title: "Scenario deleted",
        status: "success",
        duration: 3000,
      });
      onDeleteClose();
      refetch();
    } catch (err) {
      toast({
        title: "Error deleting scenario",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const confirmDelete = (scenario) => {
    setSelectedScenario(scenario);
    onDeleteOpen();
  };

  const handleTogglePublic = async (scenario) => {
    try {
      await updateScenario({
        id: scenario.id,
        isPublic: !scenario.isPublic,
      });
      toast({
        title: scenario.isPublic ? "Made private" : "Made public",
        status: "success",
        duration: 3000,
      });
      refetch();
    } catch (err) {
      toast({
        title: "Error updating scenario",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleToggleHidden = async (scenario) => {
    try {
      await updateScenario({
        id: scenario.id,
        isActive: scenario.isActive === false
      });
      toast({
        title: scenario.isActive === false ? "Scenario unhidden" : "Scenario hidden",
        status: "success",
        duration: 3000,
      });
      refetch();
    } catch (err) {
      toast({
        title: "Error updating scenario visibility",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleCopyLink = (scenario) => {
    const link = `${window.location.origin}/scenario/${scenario.id}`;
    navigator.clipboard.writeText(link);
    toast({
      title: "Link copied to clipboard",
      status: "success",
      duration: 2000,
    });
  };

  const handleRunScenario = (scenario) => {
    console.log('Opening run modal for scenario:', scenario.id);
    setRunningScenario(scenario);
    setRunModalOpen(true);
  };

  const handleTestVoiceSim = (scenario) => {
    navigate(`/voice-scenario/${scenario.id}`);
  };

  const handleRegenerateMedia = async (scenarioId) => {
    setRegeneratingScenarios(prev => ({ ...prev, [scenarioId]: true }));
    
    try {
      const result = await api.regenerateScenarioMedia({
        scenarioId,
        generatePatientImage: true,
        generateSceneVideo: true,
        generateClinicalVideos: true
      });
      
      toast({
        title: "Media generation queued",
        description: "Patient image, scene video, and clinical videos will be regenerated.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
      // Refresh the scenario list after a short delay to show updated status
      setTimeout(() => {
        refetch();
      }, 2000);
    } catch (error) {
      console.error('Error regenerating media:', error);
      toast({
        title: "Failed to regenerate media",
        description: error.message || "An error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRegeneratingScenarios(prev => ({ ...prev, [scenarioId]: false }));
    }
  };

  const handleModifyScenario = (scenario) => {
    // Build URL with query parameters from scenario data
    const params = new URLSearchParams();
    
    // Map basic fields
    if (scenario.levelOfCare) params.set('trainingLevel', scenario.levelOfCare);
    if (scenario.majorSystemInvolved && scenario.majorSystemInvolved !== 'AI-Defined') {
      params.set('primaryBodySystem', scenario.majorSystemInvolved);
    }
    
    // Use top-level metadata fields first (newer scenarios)
    if (scenario.difficulty) {
      params.set('difficulty', scenario.difficulty.toString());
    }
    
    // MOI/NOI - use top-level field first
    if (scenario.mechanismNatureOfInjury) {
      params.set('natureMechanism', scenario.mechanismNatureOfInjury);
    }
    
    // Patient age - use ageBracket if available
    if (scenario.ageBracket) {
      params.set('patientAge', scenario.ageBracket);
    }
    
    // Map from scenarioOutput if available
    if (scenario.scenarioOutput) {
      const output = scenario.scenarioOutput;
      
      // Patient demographics - only if not already set from top-level fields
      if (!params.has('patientAge') && output.patientInfo?.age) {
        // Try to map age to age range
        const age = parseInt(output.patientInfo.age);
        if (!isNaN(age)) {
          if (age <= 0.08) params.set('patientAge', 'Neonate (0-30 days)');
          else if (age <= 1) params.set('patientAge', 'Infant (1-12 months)');
          else if (age <= 2) params.set('patientAge', 'Toddler (1-2 years)');
          else if (age <= 5) params.set('patientAge', 'Preschool (3-5 years)');
          else if (age <= 12) params.set('patientAge', 'School Age (6-12 years)');
          else if (age <= 18) params.set('patientAge', 'Adolescent (13-18 years)');
          else if (age <= 64) params.set('patientAge', 'Adult (19-64 years)');
          else params.set('patientAge', 'Geriatric (65+ years)');
        }
      }
      
      if (output.patientInfo?.sex) {
        params.set('biologicalSex', output.patientInfo.sex);
      }
      
      // Chief complaint and scene
      if (output.chiefComplaint) params.set('chiefComplaint', output.chiefComplaint);
      if (output.sceneDescription) params.set('sceneDescription', output.sceneDescription);
      
      // Scene description can also come from sceneSetup.location if not already set
      if (!params.has('sceneDescription') && output.sceneSetup?.location) {
        params.set('sceneDescription', output.sceneSetup.location);
      }
      
      // Secondary complaint
      if (output.secondaryComplaint) {
        params.set('secondaryComplaint', output.secondaryComplaint);
      }
      
      // MOI/NOI - use output fields if not already set from top-level
      if (!params.has('natureMechanism')) {
        if (output.mechanismNatureOfInjury) {
          params.set('natureMechanism', output.mechanismNatureOfInjury);
        } else if (output.mechanismOfInjury) {
          params.set('natureMechanism', output.mechanismOfInjury);
        } else if (output.natureOfInjury) {
          params.set('natureMechanism', output.natureOfInjury);
        }
      }
      
      // Difficulty - use output fields if not already set from top-level
      if (!params.has('difficulty')) {
        if (output.ScenarioMetadata?.Difficulty) {
          params.set('difficulty', output.ScenarioMetadata.Difficulty.toString());
        } else if (output.difficulty) {
          params.set('difficulty', output.difficulty.toString());
        }
      }
      
      // Scene and safety fields
      if (output.sceneSetup) {
        // Safety issues map to safety consideration, NOT location
        if (output.sceneSetup.safetyIssues) {
          const safetyStr = Array.isArray(output.sceneSetup.safetyIssues)
            ? output.sceneSetup.safetyIssues.join(', ')
            : output.sceneSetup.safetyIssues;
          params.set('safetyConsideration', safetyStr);
        }
        // Location was already handled above in sceneDescription
      }
      
      // History fields
      if (output.history) {
        if (output.history.hpi) params.set('hpi', output.history.hpi);
        if (output.history.pmhx) params.set('pmhx', output.history.pmhx);
        if (output.history.medications) {
          const medsStr = Array.isArray(output.history.medications)
            ? output.history.medications.join(', ')
            : output.history.medications;
          params.set('currentMedications', medsStr);
        }
        if (output.history.allergies) {
          const allergiesStr = Array.isArray(output.history.allergies)
            ? output.history.allergies.join(', ')
            : output.history.allergies;
          params.set('allergies', allergiesStr);
        }
      }
      
      // Fallback for older format medical history
      if (!params.has('pmhx') && output.medicalHistory) {
        if (typeof output.medicalHistory === 'string') {
          params.set('pmhx', output.medicalHistory);
        } else if (output.medicalHistory.pastMedicalHistory) {
          params.set('pmhx', output.medicalHistory.pastMedicalHistory);
        }
      }
      
      // Fallback for older format allergies
      if (!params.has('allergies') && output.allergies) {
        const allergiesStr = Array.isArray(output.allergies) 
          ? output.allergies.join(', ') 
          : output.allergies;
        params.set('allergies', allergiesStr);
      }
      
      // Fallback for older format medications
      if (!params.has('currentMedications') && output.currentMedications) {
        const medsStr = Array.isArray(output.currentMedications)
          ? output.currentMedications.join(', ')
          : output.currentMedications;
        params.set('currentMedications', medsStr);
      }
      
      // Fallback for HPI/patient presentation
      if (!params.has('hpi') && output.patientPresentation) {
        params.set('hpi', output.patientPresentation);
      }
      
      // Primary Assessment fields
      if (output.primaryAssessment) {
        if (output.primaryAssessment.mentalStatus) {
          params.set('levelOfConsciousness', output.primaryAssessment.mentalStatus);
        }
        if (output.primaryAssessment.airway) {
          params.set('airwayStatus', output.primaryAssessment.airway);
        }
        if (output.primaryAssessment.breathing) {
          params.set('breathingStatus', output.primaryAssessment.breathing);
        }
        if (output.primaryAssessment.circulation) {
          params.set('circulatoryStatus', output.primaryAssessment.circulation);
        }
      }
      
      // Secondary Assessment fields
      if (output.secondaryAssessment) {
        if (output.secondaryAssessment.heent) params.set('heent', output.secondaryAssessment.heent);
        if (output.secondaryAssessment.neck) params.set('neck', output.secondaryAssessment.neck);
        if (output.secondaryAssessment.chest) params.set('chest', output.secondaryAssessment.chest);
        if (output.secondaryAssessment.respiratory) params.set('respiratory', output.secondaryAssessment.respiratory);
        if (output.secondaryAssessment.cardiovascular) params.set('cardiovascular', output.secondaryAssessment.cardiovascular);
        if (output.secondaryAssessment.abdomen) params.set('gi', output.secondaryAssessment.abdomen);
        if (output.secondaryAssessment.back) params.set('back', output.secondaryAssessment.back);
        if (output.secondaryAssessment.pelvis) params.set('pelvis', output.secondaryAssessment.pelvis);
        if (output.secondaryAssessment.neuro) params.set('nervous', output.secondaryAssessment.neuro);
        if (output.secondaryAssessment.extremities) {
          params.set('musculoskeletal', output.secondaryAssessment.extremities);
        }
        if (output.secondaryAssessment.musculoskeletal) {
          params.set('musculoskeletal', output.secondaryAssessment.musculoskeletal);
        }
      }
      
      // Fallback to physicalExamFindings for older format
      if (output.physicalExamFindings && typeof output.physicalExamFindings === 'object') {
        if (!params.has('heent') && output.physicalExamFindings.heent) {
          params.set('heent', output.physicalExamFindings.heent);
        }
        if (!params.has('respiratory') && output.physicalExamFindings.respiratory) {
          params.set('respiratory', output.physicalExamFindings.respiratory);
        }
        if (!params.has('cardiovascular') && output.physicalExamFindings.cardiovascular) {
          params.set('cardiovascular', output.physicalExamFindings.cardiovascular);
        }
        if (!params.has('gi') && output.physicalExamFindings.abdomen) {
          params.set('gi', output.physicalExamFindings.abdomen);
        }
        if (!params.has('nervous') && output.physicalExamFindings.neurological) {
          params.set('nervous', output.physicalExamFindings.neurological);
        }
        if (!params.has('musculoskeletal') && output.physicalExamFindings.musculoskeletal) {
          params.set('musculoskeletal', output.physicalExamFindings.musculoskeletal);
        }
        if (output.physicalExamFindings.skin) {
          params.set('integumentary', output.physicalExamFindings.skin);
        }
      }
    }
    
    // Navigate to scenario generator with query params
    navigate(`/scenario-generator?${params.toString()}`);
    onViewClose();
  };

  const getDescriptionPreview = (scenario) => {
    // Try to get description from markdown first
    if (scenario.description?.markdown) {
      const text = scenario.description.markdown;
      return text.length > 100 ? `${text.substring(0, 100)}...` : text;
    }
    
    // Fall back to scenarioOutput if available
    if (scenario.scenarioOutput) {
      try {
        const outputText = typeof scenario.scenarioOutput === 'string' 
          ? scenario.scenarioOutput 
          : JSON.stringify(scenario.scenarioOutput);
        const preview = outputText.replace(/[{}"[\]]/g, '').trim();
        return preview.length > 100 ? `${preview.substring(0, 100)}...` : preview;
      } catch (e) {
        return "No description available";
      }
    }
    
    return "No description available";
  };

  const menuBg = useColorModeValue("white", "gray.700");
  const menuColor = useColorModeValue("gray.800", "white");

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          Error loading scenarios: {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      {/* Header */}
      <Flex mb={6} align="center" wrap="wrap" gap={4}>
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          onClick={() => navigate('/')}
        >
          Back
        </Button>
        <VStack align="start" spacing={1} flex="1">
          <HStack spacing={3}>
            <Heading size="lg">My Scenarios</Heading>
            {hasScenarioGeneratorAccess && (
              <Badge colorScheme={
                accessSource === 'individual' ? 'blue' :
                accessSource === 'cohort' ? 'purple' :
                accessSource === 'institution' ? 'green' :
                accessSource === 'admin' ? 'red' : 'gray'
              } fontSize="sm">
                {accessSource === 'individual' && 'Individual Subscription'}
                {accessSource === 'cohort' && 'Cohort Access'}
                {accessSource === 'institution' && `Institution: ${accessInstitution}`}
                {accessSource === 'admin' && 'Admin Access'}
              </Badge>
            )}
            {canSeeSharedScenarios && (
              <Badge colorScheme="teal" fontSize="sm">
                Can See Shared Scenarios
              </Badge>
            )}
          </HStack>
        </VStack>
        <HStack spacing={3}>
          {canViewEvals && (
            <Button
              as={Link}
              to="/my-scenarios/evaluations"
              colorScheme="blue"
              variant="outline"
              leftIcon={<FaClipboardList />}
            >
              View Evaluations
            </Button>
          )}
          {hasScenarioGeneratorAccess && (
            <>
              <Button
                as={Link}
                to="/my-scenarios/sessions"
                colorScheme="teal"
                variant="outline"
                leftIcon={<FaClipboardList />}
              >
                My Sessions
              </Button>
              <Button
                as={Link}
                to="/scenario-generator"
                leftIcon={<AddIcon />}
                colorScheme="blue"
              >
                Create New
              </Button>
            </>
          )}
        </HStack>
      </Flex>

      {/* Access Information Banner */}
      {fullUser && (
        <Card mb={6} bg={useColorModeValue('blue.50', 'blue.900')} borderColor={useColorModeValue('blue.200', 'blue.600')}>
          <CardBody>
            <HStack spacing={4}>
              <Box flex="1">
                {hasScenarioGeneratorAccess ? (
                  <>
                    <HStack spacing={2} mb={1}>
                      <Text fontWeight="bold">Scenario Generator Access:</Text>
                      {accessSource === 'individual' && (
                        <Badge colorScheme="purple">Individual</Badge>
                      )}
                      {accessSource === 'cohort' && (
                        <Badge colorScheme="blue">Cohort</Badge>
                      )}
                      {accessSource === 'institution' && (
                        <Badge colorScheme="green">Institution</Badge>
                      )}
                      {accessSource === 'admin' && (
                        <Badge colorScheme="red">Admin</Badge>
                      )}
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                      {accessSource === 'individual' && 
                        "You have individual scenario generator access"}
                      {accessSource === 'cohort' && accessInstitution && 
                        `Scenario generator access via cohort membership at ${accessInstitution}`}
                      {accessSource === 'institution' && accessInstitution && 
                        `Scenario generator access via ${accessInstitution}`}
                      {accessSource === 'admin' && 
                        "Full administrative access to all features"}
                    </Text>
                    {!canSeeSharedScenarios && (
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Note: Individual subscribers only see their own scenarios. Institutional access is required to view scenarios shared by other instructors.
                      </Text>
                    )}
                  </>
                ) : canViewEvals ? (
                  <>
                    <HStack spacing={2} mb={1}>
                      <Text fontWeight="bold">Evaluation Viewing Access:</Text>
                      <Badge colorScheme="cyan">Student</Badge>
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                      You can view scenario evaluations created by your instructors
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Contact your administrator or institution for scenario generator access
                    </Text>
                  </>
                ) : (
                  <>
                    <Text fontWeight="bold" mb={1}>No Scenario Access</Text>
                    <Text fontSize="sm" color="gray.600">
                      You don't currently have access to scenario generation or evaluation viewing
                    </Text>
                  </>
                )}
              </Box>
            </HStack>
          </CardBody>
        </Card>
      )}

      {/* Credit Source Information */}
      {hasScenarioGeneratorAccess && fullUser && creditSources.length > 0 && (
        <Box mb={6} p={4} bg={useColorModeValue('blue.50', 'blue.900')} borderRadius="md" borderWidth="1px" borderColor={useColorModeValue('blue.200', 'blue.700')}>
          <Heading size="sm" mb={3}>Credit Source</Heading>
          <VStack align="stretch" spacing={2}>
            {accessSource === 'admin' && (
              <Text fontSize="sm">Admin access - unlimited scenarios</Text>
            )}
            {accessSource === 'cohort' && institutionSources.length > 0 && (
              <Text fontSize="sm">
                Using credits from: <Badge colorScheme="purple">{institutionSources[0]}</Badge>
              </Text>
            )}
            {accessSource === 'individual' && fullUser.institution && (
              <Text fontSize="sm">
                Using credits from: <Badge colorScheme="green">{fullUser.institution}</Badge> (Individual Grant)
              </Text>
            )}
            {hasSelf && (
              <Text fontSize="sm">
                Personal credits: <Badge colorScheme="blue">{fullUser.scenarioCredits || 0}</Badge>
              </Text>
            )}
            {hasMultipleSources && (
              <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')} mt={1}>
                Note: Scenarios are deducted from {institutionSources[0] || 'your primary source'}
              </Text>
            )}
          </VStack>
        </Box>
      )}

      {/* Subscription Expired Warning */}
      {fullUser && !subscriptionActive && (
        <Alert status="error" mb={6} borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <Text fontWeight="bold">Subscription Expired</Text>
            <Text fontSize="sm">
              Your subscription expired on {fullUser.subscriptionExpiration ? 
                new Date(fullUser.subscriptionExpiration).toLocaleDateString() : 'an unknown date'}.
              You cannot access saved scenarios until your subscription is renewed.
            </Text>
            {fullUser.accountType === 'institutional_student' && (
              <Text fontSize="sm" mt={2}>
                Please contact your institution administrator to renew the subscription.
              </Text>
            )}
          </Box>
        </Alert>
      )}

      {/* Credit Display for Individual Subscribers */}
      {fullUser && fullUser.accountType === 'individual' && !hasUnlimitedScenarios && (
        <Card mb={6} bg={useColorModeValue('gray.50', 'gray.800')} borderColor={useColorModeValue('gray.200', 'gray.600')}>
          <CardBody>
            <HStack spacing={6}>
              <Box flex="1">
                <Text fontSize="sm" color="gray.500">Scenario Credits</Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {fullUser.scenarioCredits || 0} remaining
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {fullUser.scenariosUsedThisYear || 0} used this year
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Renews</Text>
                <Text fontSize="md" fontWeight="semibold">
                  {fullUser.scenarioCreditsResetDate ? 
                    new Date(fullUser.scenarioCreditsResetDate).toLocaleDateString() : 'Never'}
                </Text>
              </Box>
              <Button
                colorScheme="blue"
                size="sm"
                onClick={() => {
                  // TODO: Link to vendor's payment page for purchasing more credits
                  window.open('https://vendor-payment-link.com', '_blank');
                }}
              >
                Purchase More Credits
              </Button>
            </HStack>
          </CardBody>
        </Card>
      )}

      {/* Unlimited Badge */}
      {hasUnlimitedScenarios && (
        <Alert status="success" mb={6} borderRadius="md">
          <AlertIcon />
          <Text>🎉 You have unlimited scenario generation!</Text>
        </Alert>
      )}

      {/* Search and Filters - Only show if user has some access */}
      {(hasScenarioGeneratorAccess || canViewEvals) && (
        <Stack spacing={4} mb={6}>
          <Input
            placeholder="Search scenarios..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<SearchIcon />}
          />

          <HStack spacing={4} wrap="wrap">
            <Select
              value={levelOfCareFilter}
              onChange={(e) => setLevelOfCareFilter(e.target.value)}
              maxW="200px"
            >
              {LEVELS_OF_CARE.map((level) => (
                <option key={level} value={level}>
                  {level === "All" ? "All Levels" : level.replace(/_/g, " ")}
                </option>
              ))}
            </Select>

            <Select
              value={systemFilter}
              onChange={(e) => setSystemFilter(e.target.value)}
              maxW="200px"
            >
              {MAJOR_SYSTEMS.map((system) => (
                <option key={system} value={system}>
                  {system === "All" ? "All Systems" : system}
              </option>
            ))}
          </Select>

            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              maxW="220px"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  Sort: {option.label}
                </option>
              ))}
            </Select>

            <FormControl display="flex" alignItems="center" maxW="250px">
              <FormLabel htmlFor="show-public" mb="0">
                Show Public Scenarios
              </FormLabel>
              <Switch
                id="show-public"
                isChecked={showPublic}
                onChange={(e) => setShowPublic(e.target.checked)}
              />
            </FormControl>

            <FormControl display="flex" alignItems="center" maxW="250px">
              <FormLabel htmlFor="show-hidden" mb="0">
                Show Hidden Scenarios
              </FormLabel>
              <Switch
                id="show-hidden"
                isChecked={showHiddenScenarios}
                onChange={(e) => setShowHiddenScenarios(e.target.checked)}
              />
            </FormControl>
          </HStack>
        </Stack>
      )}

      <Divider mb={6} />

      {/* Shared Scenario Visibility Info */}
      {hasScenarioGeneratorAccess && subscriptionActive && !showPublic && (
        <>
          {!canSeeSharedScenarios ? (
            <Alert status="info" mb={4}>
              <AlertIcon />
              You are using an individual subscription. You can only see your own scenarios.
            </Alert>
          ) : (
            <Alert status="success" mb={4}>
              <AlertIcon />
              You can see scenarios shared by other instructors at {accessInstitution}.
            </Alert>
          )}
        </>
      )}

      {/* Only show content if user has scenario access OR can view evaluations */}
      {(hasScenarioGeneratorAccess || canViewEvals) && subscriptionActive ? (
        <>
          {/* Loading State */}
          {fetching && !filteredData && hasScenarioGeneratorAccess && (
            <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={6}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Box key={i} bg="white" borderRadius="md" p={4} shadow="base" border="1px solid" borderColor="gray.200">
                  <Skeleton height="20px" mb={2} />
                  <SkeletonText mt={4} noOfLines={3} spacing={4} />
                </Box>
              ))}
            </Grid>
          )}

          {/* Empty State */}
          {!fetching && (!filteredData || filteredData.length === 0) && hasScenarioGeneratorAccess && (
            <Box textAlign="center" py={10}>
              <Heading size="md" mb={4}>
                No scenarios found
              </Heading>
              <Text color="gray.600" mb={6}>
                {searchQuery || levelOfCareFilter !== "All" || systemFilter !== "All" || showHiddenScenarios
                  ? "Try adjusting your filters"
                  : "Get started by creating your first clinical scenario"}
              </Text>
              <Button
                as={Link}
                to="/scenario-generator"
                leftIcon={<AddIcon />}
                colorScheme="blue"
              >
                Create Your First Scenario
              </Button>
            </Box>
          )}

          {/* Student Eval Viewing Only State */}
          {!hasScenarioGeneratorAccess && canViewEvals && (
            <Container maxW="container.lg" py={8}>
              <VStack spacing={6} align="center">
                <Icon as={FaClipboardList} boxSize={16} color="blue.500" />
                <Heading size="lg">Scenario Evaluations</Heading>
                <Alert status="info" maxW="md">
                  <AlertIcon />
                  <VStack align="start" spacing={2}>
                    <Text>
                      Your institution ({accessInstitution}) has enabled student evaluation viewing.
                    </Text>
                    <Text>
                      You can view scenario evaluations in read-only mode but cannot generate new scenarios.
                    </Text>
                    <Text fontWeight="bold">
                      Access granted via: {accessSource}
                    </Text>
                  </VStack>
                </Alert>
                <Button
                  as={Link}
                  to="/my-scenarios/evaluations"
                  colorScheme="blue"
                  leftIcon={<FaClipboardList />}
                  size="lg"
                >
                  View My Evaluations
                </Button>
              </VStack>
            </Container>
          )}

          {/* Scenario Grid - Only show if user has generator access */}
          {!fetching && filteredData && filteredData.length > 0 && hasScenarioGeneratorAccess && (
        <>
          <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={6}>
            {filteredData.map((scenario) => (
              <Card
                key={scenario.id}
                bg={isSharedScenario(scenario) ? "purple.800" : undefined}
                borderColor={isSharedScenario(scenario) ? "purple.600" : "gray.200"}
                borderWidth={isSharedScenario(scenario) ? "2px" : "1px"}
                _hover={{ shadow: "lg", transform: "translateY(-2px)" }}
                transition="all 0.2s"
                position="relative"
                zIndex={openMenuId === scenario.id ? 1000 : 1}
                isolation="isolate"
              >
                <CardBody>
                  <VStack align="stretch" spacing={3}>
                    <Heading size="md" noOfLines={2}>
                      {typeof scenario.title === 'string' ? scenario.title : (scenario.title?.markdown || "Untitled Scenario")}
                    </Heading>

                    <HStack wrap="wrap">
                      <Badge colorScheme="gray" variant="outline" fontSize="xs">
                        ID: {scenario.id}
                      </Badge>
                      {isSharedScenario(scenario) && (
                        <Badge colorScheme="purple" variant="solid">
                          Shared by {scenario.createdBy.firstName} {scenario.createdBy.lastName}
                        </Badge>
                      )}
                      {scenario.ageBracket && (
                        <Badge colorScheme="cyan">
                          {scenario.ageBracket.replace(/_/g, " ").replace(/\s*\([^)]*\)/g, "")}
                        </Badge>
                      )}
                      {scenario.difficulty && (
                        <Badge colorScheme="orange">
                          Difficulty: {scenario.difficulty}/5
                        </Badge>
                      )}
                      <Badge colorScheme="purple">
                        {scenario.levelOfCare ? scenario.levelOfCare.replace(/_/g, " ") : "N/A"}
                      </Badge>
                      {scenario.scenarioOutput?.patientInfo?.sex && (
                        <Badge colorScheme="teal">
                          {scenario.scenarioOutput.patientInfo.sex}
                        </Badge>
                      )}
                      {scenario.isPublic && (
                        <Badge colorScheme="green">Public</Badge>
                      )}
                      {scenario.isActive === false && (
                        <Badge colorScheme="red" variant="solid">Hidden</Badge>
                      )}
                      <MediaStatusBadge 
                        mediaGenerationStatus={scenario.mediaGenerationStatus}
                        mediaGenerationEnabled={scenario.mediaGenerationEnabled}
                      />
                    </HStack>

                    {scenario.mechanismNatureOfInjury && (
                      <Text fontSize="sm" color="gray.600" fontWeight="semibold" noOfLines={2}>
                        MOI/NOI: {scenario.mechanismNatureOfInjury}
                      </Text>
                    )}

                    {scenario.majorSystemInvolved && scenario.majorSystemInvolved !== 'AI-Defined' && (
                      <Text fontSize="sm" color="gray.500">
                        System: {scenario.majorSystemInvolved}
                      </Text>
                    )}

                    <Text fontSize="sm" noOfLines={2}>
                      {getDescriptionPreview(scenario)}
                    </Text>

                    <HStack fontSize="xs" color="gray.500" justify="space-between">
                      <Text>
                        Created: {new Date(scenario.createdAt).toLocaleDateString()}
                      </Text>
                      {scenario.institution && (
                        <Text noOfLines={1}>{scenario.institution}</Text>
                      )}
                    </HStack>
                  </VStack>
                </CardBody>

                <CardFooter>
                  <HStack spacing={2} width="100%" onClick={(e) => e.stopPropagation()}>
                    {!useCompactButtons ? (
                      <>
                        <Button
                          size="sm"
                          leftIcon={<ExternalLinkIcon />}
                          onClick={() => navigate(`/scenario/${scenario.id}`)}
                          flex={1}
                        >
                          Open
                        </Button>
                        <IconButton
                          icon={<ViewIcon />}
                          size="sm"
                          aria-label="Preview"
                          onClick={() => handleViewScenario(scenario)}
                        />
                        <Menu 
                          isOpen={openMenuId === scenario.id}
                          onClose={() => setOpenMenuId(null)}
                          strategy="fixed"
                          placement="bottom-end"
                          gutter={8}
                          isLazy
                        >
                          <MenuButton
                            as={IconButton}
                            icon={<ChevronDownIcon />}
                            size="sm"
                            aria-label="More options"
                            onClick={(e) => {
                              console.log('Menu button clicked for scenario:', scenario.id);
                              e.stopPropagation();
                              setOpenMenuId(openMenuId === scenario.id ? null : scenario.id);
                            }}
                          />
                          <MenuList
                            bg={useColorModeValue('white', 'gray.700')}
                            color={useColorModeValue('gray.800', 'white')}
                            boxShadow="2xl"
                            border="1px solid"
                            borderColor={useColorModeValue('gray.200', 'gray.600')}
                            zIndex={10000}
                            overflow="visible"
                          >
                            <MenuItem
                              icon={<EditIcon />}
                              onClick={() => handleEditDescription(scenario)}
                              _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                            >
                              Edit Description
                            </MenuItem>
                            <MenuItem
                              icon={<CopyIcon />}
                              onClick={() => handleDuplicate(scenario)}
                              _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                            >
                              Duplicate
                            </MenuItem>
                            <MenuItem
                              icon={<Icon as={FiShare2} />}
                              onClick={() => {
                                setShareScenario(scenario);
                                onShareOpen();
                              }}
                              color="blue.600"
                              _hover={{ bg: useColorModeValue('blue.50', 'blue.900') }}
                            >
                              Share with Evaluators
                            </MenuItem>
                            {isInstructor && (
                              <MenuItem
                                icon={<Icon as={FiShare2} />}
                                onClick={() => {
                                  setVoiceSimScenario(scenario);
                                  onVoiceSimShareOpen();
                                }}
                                color="teal.600"
                                _hover={{ bg: useColorModeValue('teal.50', 'teal.900') }}
                              >
                                Share as Voice Sim
                              </MenuItem>
                            )}
                            {(isInstructor || isAdminUser) && (
                              <MenuItem
                                icon={<Icon as={FaPlay} />}
                                onClick={() => handleTestVoiceSim(scenario)}
                                color="purple.700"
                                _hover={{ bg: useColorModeValue('purple.50', 'purple.900') }}
                              >
                                Test Voice Sim
                              </MenuItem>
                            )}
                            {isInstructor && (
                              <MenuItem
                                icon={<Icon as={FaPlay} />}
                                onClick={() => handleRunScenario(scenario)}
                                color="gray.800"
                                _hover={{ bg: 'gray.100' }}
                              >
                                Run Scenario
                              </MenuItem>
                            )}
                            <MenuItem
                              icon={<ExternalLinkIcon />}
                              onClick={() => handleCopyLink(scenario)}
                              _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                            >
                              Copy Link
                            </MenuItem>
                            <MenuItem 
                              onClick={() => handleTogglePublic(scenario)}
                              _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                            >
                              {scenario.isPublic ? "Make Private" : "Make Public"}
                            </MenuItem>
                            <MenuItem
                              onClick={() => handleToggleHidden(scenario)}
                              _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                            >
                              {scenario.isActive === false ? "Unhide Scenario" : "Hide Scenario"}
                            </MenuItem>
                            {user?.roles?.some(role => 
                              role === 'system-admin' || role?.key === 'system-admin'
                            ) && (
                              <MenuItem
                                icon={<RepeatIcon />}
                                onClick={() => handleRegenerateMedia(scenario.id)}
                                isDisabled={regeneratingScenarios[scenario.id]}
                                _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                              >
                                {regeneratingScenarios[scenario.id] ? "Regenerating..." : "Regenerate Media"}
                              </MenuItem>
                            )}
                            <MenuItem
                              icon={<DeleteIcon />}
                              color="red.500"
                              onClick={() => confirmDelete(scenario)}
                              _hover={{ bg: useColorModeValue('red.50', 'red.900') }}
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          leftIcon={<ExternalLinkIcon />}
                          onClick={() => navigate(`/scenario/${scenario.id}`)}
                          flex={1}
                        >
                          Open
                        </Button>
                        <Menu 
                          isOpen={openMenuId === scenario.id}
                          onClose={() => setOpenMenuId(null)}
                          strategy="fixed"
                          placement="bottom-end"
                          gutter={8}
                          isLazy
                        >
                          <MenuButton
                            as={IconButton}
                            icon={<ChevronDownIcon />}
                            size="sm"
                            aria-label="More options"
                            onClick={(e) => {
                              console.log('Menu button clicked for scenario:', scenario.id);
                              e.stopPropagation();
                              setOpenMenuId(openMenuId === scenario.id ? null : scenario.id);
                            }}
                          />
                          <MenuList
                            bg={useColorModeValue('white', 'gray.700')}
                            color={useColorModeValue('gray.800', 'white')}
                            boxShadow="2xl"
                            border="1px solid"
                            borderColor={useColorModeValue('gray.200', 'gray.600')}
                            zIndex={10000}
                            overflow="visible"
                          >
                            <MenuItem
                              icon={<ViewIcon />}
                              onClick={() => handleViewScenario(scenario)}
                              _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                            >
                              Quick View
                            </MenuItem>
                            <MenuItem
                              icon={<EditIcon />}
                              onClick={() => handleEditDescription(scenario)}
                              _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                            >
                              Edit Description
                            </MenuItem>
                            <MenuItem
                              icon={<CopyIcon />}
                              onClick={() => handleDuplicate(scenario)}
                              _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                            >
                              Duplicate
                            </MenuItem>
                            <MenuItem
                              icon={<Icon as={FiShare2} />}
                              onClick={() => {
                                setShareScenario(scenario);
                                onShareOpen();
                              }}
                              color="blue.600"
                              _hover={{ bg: useColorModeValue('blue.50', 'blue.900') }}
                            >
                              Share with Evaluators
                            </MenuItem>
                            {isInstructor && (
                              <MenuItem
                                icon={<Icon as={FiShare2} />}
                                onClick={() => {
                                  setVoiceSimScenario(scenario);
                                  onVoiceSimShareOpen();
                                }}
                                color="teal.600"
                                _hover={{ bg: useColorModeValue('teal.50', 'teal.900') }}
                              >
                                Share as Voice Sim
                              </MenuItem>
                            )}
                            {(isInstructor || isAdminUser) && (
                              <MenuItem
                                icon={<Icon as={FaPlay} />}
                                onClick={() => handleTestVoiceSim(scenario)}
                                color="purple.700"
                                _hover={{ bg: useColorModeValue('purple.50', 'purple.900') }}
                              >
                                Test Voice Sim
                              </MenuItem>
                            )}
                            {isInstructor && (
                              <MenuItem
                                icon={<Icon as={FaPlay} />}
                                onClick={() => handleRunScenario(scenario)}
                                color="gray.800"
                                _hover={{ bg: 'gray.100' }}
                              >
                                Run Scenario
                              </MenuItem>
                            )}
                            <MenuItem
                              icon={<ExternalLinkIcon />}
                              onClick={() => handleCopyLink(scenario)}
                              _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                            >
                              Copy Link
                            </MenuItem>
                            <MenuItem 
                              onClick={() => handleTogglePublic(scenario)}
                              _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                            >
                              {scenario.isPublic ? "Make Private" : "Make Public"}
                            </MenuItem>
                            <MenuItem
                              onClick={() => handleToggleHidden(scenario)}
                              _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                            >
                              {scenario.isActive === false ? "Unhide Scenario" : "Hide Scenario"}
                            </MenuItem>
                            {user?.roles?.some(role => 
                              role === 'system-admin' || role?.key === 'system-admin'
                            ) && (
                              <MenuItem
                                icon={<RepeatIcon />}
                                onClick={() => handleRegenerateMedia(scenario.id)}
                                isDisabled={regeneratingScenarios[scenario.id]}
                                _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                              >
                                {regeneratingScenarios[scenario.id] ? "Regenerating..." : "Regenerate Media"}
                              </MenuItem>
                            )}
                            <MenuItem
                              icon={<DeleteIcon />}
                              color="red.500"
                              onClick={() => confirmDelete(scenario)}
                              _hover={{ bg: useColorModeValue('red.50', 'red.900') }}
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </>
                    )}
                  </HStack>
                </CardFooter>
              </Card>
            ))}
          </Grid>

            {/* Pagination */}
            {filteredData.hasNextPage && (
              <Flex justify="center" mt={8}>
                <Button
                  onClick={() => {
                    setCurrentPage((p) => p + 1);
                    refetch();
                  }}
                  isLoading={fetching}
                >
                  Load More
                </Button>
              </Flex>
            )}
          </>
        )}
      </>
      ) : !hasScenarioGeneratorAccess && !canViewEvals ? (
        <Container maxW="container.lg" py={8}>
          <VStack spacing={6} align="center">
            <Icon as={FaClipboardList} boxSize={16} color="gray.400" />
            <Heading size="lg">Scenario Generator</Heading>
            
            {accessSource === null && (
              <>
                <Text fontSize="lg" color="gray.400" textAlign="center">
                  You don't currently have access to the scenario generator.
                </Text>
                <Alert status="info" maxW="md">
                  <AlertIcon />
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="bold">To gain access, you need one of:</Text>
                    <Text>• Individual subscription (independent access)</Text>
                    <Text>• Cohort membership with scenario generator access</Text>
                    <Text>• Institution-wide access (non-students)</Text>
                    <Text>• Student evaluation viewing (read-only for students)</Text>
                  </VStack>
                </Alert>
              </>
            )}
          </VStack>
        </Container>
      ) : (
        <Box textAlign="center" py={20}>
          <Icon as={FaClipboardList} boxSize={16} color="gray.500" mb={4} />
          <Heading size="lg" mb={2}>Subscription Required</Heading>
          <Text color="gray.500" mb={6}>
            Renew your subscription to access your saved scenarios
          </Text>
        </Box>
      )}

      {/* View Scenario Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Text>{typeof selectedScenario?.title === 'string' ? selectedScenario.title : (selectedScenario?.title?.markdown || "Untitled Scenario")}</Text>
              <Badge colorScheme="blue">Preview</Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedScenario && selectedScenario.scenarioOutput ? (
              <ScenarioDisplay scenario={selectedScenario.scenarioOutput} previewMode={true} />
            ) : (
              <VStack align="stretch" spacing={4}>
                <HStack>
                  <Badge colorScheme="purple">
                    {selectedScenario?.levelOfCare ? selectedScenario.levelOfCare.replace(/_/g, " ") : "N/A"}
                  </Badge>
                  {selectedScenario?.ageRange && (
                    <Badge colorScheme="blue">{selectedScenario.ageRange}</Badge>
                  )}
                  {selectedScenario?.majorSystemInvolved && (
                    <Badge>{selectedScenario.majorSystemInvolved}</Badge>
                  )}
                </HStack>

                <Box>
                  <Text fontWeight="bold" mb={2}>Description:</Text>
                  <Text whiteSpace="pre-wrap">
                    {selectedScenario?.description?.markdown || "No description available"}
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" mb={2}>No formatted scenario data available</Text>
                  <Text fontSize="sm" color="gray.500">
                    This scenario may have been created before the new format was implemented.
                  </Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3} width="100%" justify="space-between">
              <Button 
                colorScheme="blue" 
                onClick={() => {
                  navigate(`/scenario/${selectedScenario.id}`);
                  onViewClose();
                }}
              >
                Open Scenario
              </Button>
              <Button variant="ghost" onClick={onViewClose}>Close</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Description Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Description</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Add scenario description (markdown supported)..."
              rows={8}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveDescription}
              isLoading={updateResult.fetching}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Scenario
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete "{selectedScenario?.title}"? This action cannot be
              undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                isLoading={deleteResult.fetching}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Share Scenario Modal */}
      <ShareScenarioModal
        isOpen={isShareOpen}
        onClose={() => {
          onShareClose();
          setShareScenario(null);
        }}
        scenario={shareScenario}
        cohort={null}
        user={user}
      />

      {/* Run Scenario Modal */}
      {runModalOpen && runningScenario && (
        <RunScenarioModal
          isOpen={runModalOpen}
          onClose={() => {
            setRunModalOpen(false);
            setRunningScenario(null);
          }}
          scenario={runningScenario}
          instructorCohorts={userCohorts}
        />
      )}

      {/* Voice Sim Share Modal */}
      {voiceSimScenario && (
        <VoiceSimShareModal
          isOpen={isVoiceSimShareOpen}
          onClose={() => {
            onVoiceSimShareClose();
            setVoiceSimScenario(null);
          }}
          scenario={voiceSimScenario}
          instructorCohorts={userCohorts}
        />
      )}
    </Container>
  );
}
